import React, { createContext, ReactNode, useContext, useState } from 'react';
import {
    ODataQuery,
    useListContext,
    UseListContextReturn,
} from './useListContext';
import { SupplementFilterState } from '../../types/supplement/supplementsTypes';
import useTimeZone from '../../customHooks/useTimeZone';
import { getSupplementsInitialStateFilters } from '../../utils/supplements/supplementUtils';
import { ListViewType, sortDirections } from '../../types/lists/listTypes';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import { IComplementaryDataState } from '../../types/taxes/taxesTypes';
import { useODataMultipleQueries } from '../../customHooks/useODataMultipleQueries';
import { ISupplierHotelStopSale } from '../../types/entities/ISupplierHotelStopSale';
import { IStopSaleListState } from '../../types/stopSale/stopSalesTypes';

interface StopSalesContextType
    extends UseListContextReturn<IStopSaleListState, SupplementFilterState> {
    visibleView: ListViewType;
    changeVisibleView: (view: ListViewType) => void;
    supplierHotels: IComplementaryDataState;
}

const ContextStopSales = createContext<StopSalesContextType | undefined>(
    undefined
);

export const ProviderStopSales = ({ children }: { children: ReactNode }) => {
    const timezone = useTimeZone();
    const INITIAL_STATE_FILTERS = getSupplementsInitialStateFilters(timezone);
    const ITEMS_PER_PAGE = 40;
    const {
        items,
        filters,
        sortDir,
        sortedBy,
        handleFilterChange,
        resetFilters,
        sortByField,
        handleSearch,
        handleClearFilters,
        handleScroll,
        isLoading,
        totalResults,
        currentResults,
        page,
    } = useListContext<
        ISupplierHotelStopSale,
        IStopSaleListState,
        SupplementFilterState
    >({
        initialFilters: INITIAL_STATE_FILTERS,
        buildQuery: (filtersToOdata): ODataQuery => {
            return {
                entityName: readOnlyEntities.SupplierHotelStopSale,
                query: {
                    filter: filtersToOdata,
                    select: [
                        'Id',
                        'Enabled',
                        'FromDate',
                        'ToDate',
                        'IdSupplierHotel',
                        'SupplierHotel/Name',
                        'IdSupplierHotelRoomType',
                        'SupplierHotelRoomType/Name',
                        'IdSupplierHotelMealPlan',
                        'SupplierHotelMealPlan/Name',
                        'IdSupplierHotelRatePlan',
                        'SupplierHotelRatePlan/Name',
                    ],
                    expand: [
                        readOnlyEntities.SupplierHotel,
                        readOnlyEntities.SupplierHotelRoomType,
                        readOnlyEntities.SupplierHotelMealPlan,
                        readOnlyEntities.SupplierHotelRatePlan,
                    ],
                    orderBy: `${sortedBy} ${sortDir}`,
                    top: ITEMS_PER_PAGE,
                },
            };
        },
        adaptItem: (raw: ISupplierHotelStopSale): IStopSaleListState => ({
            Id: raw.Id,
            Enabled: raw.Enabled,
            FromDate: raw.FromDate,
            ToDate: raw.ToDate,
            IdSupplierHotel: raw.IdSupplierHotel,
            SupplierHotel: raw.SupplierHotel.Name,
            IdSupplierHotelRoomType: raw.IdSupplierHotelRoomType,
            SupplierHotelRoomType: raw.SupplierHotelRoomType.Name,
            IdSupplierHotelMealPlan: raw.IdSupplierHotelMealPlan,
            SupplierHotelMealPlan: raw.SupplierHotelMealPlan.Name,
            IdSupplierHotelRatePlan: raw.IdSupplierHotelRatePlan,
            SupplierHotelRatePlan: raw.SupplierHotelRatePlan.Name,
        }),
        itemsPerPage: ITEMS_PER_PAGE,
        initialSortBy: 'DateLastUpdated',
        initialSortDir: sortDirections.DESC,
    });

    const [visibleView, setVisibleView] = useState<ListViewType>(
        ListViewType.TABLE
    );
    const changeVisibleView = (view: ListViewType) => {
        setVisibleView(view);
    };

    const independentQueries = [
        {
            key: 'supplierHotels',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotel,
                select: ['Id', 'Name', 'IdCurrency'],
                orderBy: 'Name asc',
            },
        },
    ];

    const { supplierHotels } = useODataMultipleQueries({
        queries: independentQueries,
        dependencies: [],
    });

    return (
        <ContextStopSales.Provider
            value={{
                items,
                filters,
                sortDir,
                sortedBy,
                handleFilterChange,
                sortByField,
                handleSearch,
                handleClearFilters,
                handleScroll,
                isLoading,
                totalResults,
                currentResults,
                page,
                visibleView,
                resetFilters,
                changeVisibleView,
                supplierHotels,
            }}
        >
            {children}
        </ContextStopSales.Provider>
    );
};

export const useStopSalesList = () => {
    const context = useContext(ContextStopSales);
    if (context === undefined) {
        throw new Error(
            'useStopSalesList must be used within a ProviderStopSales'
        );
    }
    return context;
};

export const useStopSalesComplementaryData = () => {
    const { supplierHotels } = useContext(
        ContextStopSales
    ) as StopSalesContextType;

    return {
        supplierHotels,
    };
};
